import { jsxs, jsx } from "react/jsx-runtime";
import { Box, CloseButton } from "@zillow/constellation-10";
import { isLocalStorageSupported } from "@zillow/is-local-storage-supported";
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { BANNER_MOUNT_ID, BANNER_SSR_ATTR, DEFAULT_BANNER_LABEL, DEFAULT_CLOSE_LABEL, DISMISSAL_KEY_PREFIX } from "./Banner.constants.esm.js";
const dismissalKey = (bannerId) => `${DISMISSAL_KEY_PREFIX}${bannerId}`;
const readDismissal = (bannerId) => {
  if (!isLocalStorageSupported()) {
    return false;
  }
  try {
    return window.localStorage.getItem(dismissalKey(bannerId)) === "true";
  } catch {
    return false;
  }
};
const hasStoredDismissal = (bannerId, dismissible, persistDismissal) => dismissible && persistDismissal && readDismissal(bannerId);
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;
const writeDismissal = (bannerId) => {
  if (!isLocalStorageSupported()) {
    return;
  }
  try {
    window.localStorage.setItem(dismissalKey(bannerId), "true");
  } catch {
  }
};
function Banner({
  bannerId,
  children,
  dismissible = false,
  darkBackground = false,
  closeLabel = DEFAULT_CLOSE_LABEL,
  onClose,
  onImpression,
  persistDismissal = true,
  disablePortal = false,
  css: cssProp
}) {
  const [isDismissed, setIsDismissed] = useState(() => hasStoredDismissal(bannerId, dismissible, persistDismissal));
  const [portalTarget, setPortalTarget] = useState(null);
  const containerRef = useRef(null);
  const createdMountRef = useRef(false);
  const hasTrackedImpression = useRef(false);
  const [trackedBannerId, setTrackedBannerId] = useState(bannerId);
  if (bannerId !== trackedBannerId) {
    setTrackedBannerId(bannerId);
    setIsDismissed(hasStoredDismissal(bannerId, dismissible, persistDismissal));
    hasTrackedImpression.current = false;
  }
  useIsomorphicLayoutEffect(() => {
    var _a;
    if (disablePortal) {
      return;
    }
    let node = document.getElementById(BANNER_MOUNT_ID);
    createdMountRef.current = !node;
    if (!node) {
      node = document.createElement("div");
      node.id = BANNER_MOUNT_ID;
      document.body.insertBefore(node, document.body.firstChild);
    }
    (_a = node.querySelector(`:scope > [${BANNER_SSR_ATTR}]`)) == null ? void 0 : _a.remove();
    setPortalTarget(node);
    return () => setPortalTarget(null);
  }, [disablePortal]);
  useEffect(() => {
    if (!portalTarget || !createdMountRef.current) {
      return;
    }
    return () => {
      if (!portalTarget.hasChildNodes()) {
        portalTarget.remove();
      }
    };
  }, [portalTarget]);
  useEffect(() => {
    if (hasStoredDismissal(bannerId, dismissible, persistDismissal)) {
      setIsDismissed(true);
      return;
    }
    if (!onImpression || isDismissed || hasTrackedImpression.current) {
      return;
    }
    if (!portalTarget && !disablePortal) {
      return;
    }
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      hasTrackedImpression.current = true;
      onImpression();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        hasTrackedImpression.current = true;
        onImpression();
        observer.disconnect();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [bannerId, dismissible, disablePortal, isDismissed, onImpression, persistDismissal, portalTarget]);
  const handleClose = useCallback(() => {
    setIsDismissed(true);
    if (persistDismissal) {
      writeDismissal(bannerId);
    }
    onClose == null ? void 0 : onClose();
  }, [bannerId, onClose, persistDismissal]);
  if (isDismissed) {
    return null;
  }
  const closeControlCss = {
    position: "absolute",
    insetBlockStart: "tighter",
    insetInlineEnd: "tighter"
  };
  let closeControl = null;
  if (dismissible && darkBackground) {
    closeControl = /* @__PURE__ */ jsx(CloseButton, { onImpact: true, title: closeLabel, onClick: handleClose, css: closeControlCss });
  } else if (dismissible) {
    closeControl = /* @__PURE__ */ jsx(CloseButton, { title: closeLabel, onClick: handleClose, css: closeControlCss });
  }
  const bar = /* @__PURE__ */ jsxs(Box, { ref: containerRef, role: "region", "aria-label": DEFAULT_BANNER_LABEL, "data-testid": "banner", "data-banner-id": bannerId, css: {
    position: "relative",
    width: "100%",
    ...cssProp
  }, children: [
    children,
    closeControl
  ] });
  if (disablePortal) {
    return bar;
  }
  return portalTarget ? createPortal(bar, portalTarget) : null;
}
export {
  Banner as default
};
